import React, { useRef, useState } from 'react';
import { Camera, Trash2, X, UploadCloud, ImageIcon } from 'lucide-react';
import { uploadInvestorLogo, deleteInvestorLogo } from '@/api/investors';
import toast from 'react-hot-toast';
import { confirmToast } from '@/lib/notify';

interface Props {
  uuidId: string;
  initialLogoUrl?: string | null;
  onLogoChange?: (newUrl: string | null) => void;
}

export default function InvestorLogo({ uuidId, initialLogoUrl, onLogoChange }: Props) {
  const [logoUrl, setLogoUrl] = useState<string | null>(initialLogoUrl || null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Sadece resim dosyaları seçilebilir');
      return;
    }

    try {
      setIsUploading(true);
      const result = await uploadInvestorLogo(uuidId, file);
      setLogoUrl(result.logo_url);
      onLogoChange?.(result.logo_url);
      toast.success('Logo başarıyla güncellendi');
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error('Logo yüklenirken bir hata oluştu');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!(await confirmToast('Logo silinsin mi?'))) return;

    try {
      setIsUploading(true);
      await deleteInvestorLogo(uuidId);
      setLogoUrl(null);
      onLogoChange?.(null);
      toast.success('Logo silindi');
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error('Logo silinirken bir hata oluştu');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="relative group overflow-visible">
      <div 
        onClick={() => setIsModalOpen(true)}
        className="h-20 w-20 rounded-2xl border border-slate-200 bg-white flex items-center justify-center overflow-hidden cursor-pointer hover:border-blue-400 transition-all shadow-sm relative"
      >
        {logoUrl ? (
          <img src={logoUrl} alt="Investor Logo" className="h-full w-full object-contain p-2" />
        ) : (
          <div className="flex flex-col items-center gap-1">
             <ImageIcon className="h-8 w-8 text-slate-300" />
             <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">LOGO</span>
          </div>
        )}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
          <Camera className="h-6 w-6 text-white" />
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-800">Yatırımcı Logosu</h3>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X className="h-6 w-6 text-slate-400" />
              </button>
            </div>

            <div className="flex flex-col items-center gap-8">
              <div className="relative h-48 w-48 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden group/preview">
                {logoUrl ? (
                  <>
                    <img src={logoUrl} alt="Current Logo" className="h-full w-full object-contain p-4" />
                    <button
                      onClick={handleDelete}
                      disabled={isUploading}
                      className="absolute top-2 right-2 p-2 bg-rose-500 text-white rounded-lg opacity-0 group-hover/preview:opacity-100 transition-all hover:bg-rose-600 shadow-lg"
                      title="Logoyu Sil"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </>
                ) : (
                  <div className="text-center p-6">
                    <ImageIcon className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-400 font-medium">Logo tanımlanmamış</p>
                  </div>
                )}
                {isUploading && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                    <div className="h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>

              <div className="w-full flex flex-col gap-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 active:scale-[0.98] transition-all shadow-lg shadow-blue-200 disabled:opacity-50"
                >
                  <UploadCloud className="h-5 w-5" />
                  {logoUrl ? 'Logoyu Değiştir' : 'Logo Yükle'}
                </button>
                
                {logoUrl && (
                   <button
                   onClick={handleDelete}
                   disabled={isUploading}
                   className="flex items-center justify-center gap-2 w-full py-4 rounded-2xl bg-rose-50 text-rose-600 font-bold text-sm hover:bg-rose-100 active:scale-[0.98] transition-all disabled:opacity-50"
                 >
                   <Trash2 className="h-5 w-5" />
                   Logoyu Tamamen Kaldır
                 </button>
                )}
              </div>
              
              <p className="text-xs text-slate-400 text-center">
                Desteklenen formatlar: PNG, JPG, JPEG, WEBP. Maksimum 10MB.
              </p>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              className="hidden"
            />
          </div>
        </div>
      )}
    </div>
  );
}
